/**
 * Flatpickr Theme Manager
 *
 * Handles global theme management for flatpickr: CSS loading, theme detection,
 * and inline style injection. Used by FlatpickrController.
 */

const DARK_THEME_LINK_ID = 'flatpickr-dark-theme-css'
const DEFAULT_THEME_LINK_ID = 'flatpickr-default-theme-css'
const LIGHT_STYLE_ID = 'flatpickr-light-theme-css'
const MODAL_ZINDEX_ID = 'flatpickr-modal-z-index'

const DARK_CSS_URL = 'https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/themes/dark.css'
const DEFAULT_CSS_URL = 'https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/flatpickr.min.css'

/**
 * Detect current application theme
 */
export function getTheme() {
  const explicit = document.documentElement.getAttribute('data-bs-theme')
  if (explicit === 'dark' || explicit === 'light') return explicit

  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}

/**
 * Update flatpickr CSS to match current theme. Returns Promise that resolves when ready.
 */
export function updateThemeCSS() {
  return new Promise((resolve) => {
    const theme = getTheme()

    if (theme === 'dark') {
      removeElementById(DEFAULT_THEME_LINK_ID)

      let link = document.getElementById(DARK_THEME_LINK_ID)
      if (!link) {
        link = createElement('link', {
          id: DARK_THEME_LINK_ID,
          rel: 'stylesheet',
          href: DARK_CSS_URL,
          crossOrigin: 'anonymous'
        })
        link.onload = resolve
        link.onerror = resolve
        document.head.appendChild(link)
      } else {
        link.disabled = false
        resolve()
      }
    } else {
      removeElementById(DARK_THEME_LINK_ID)

      let link = document.getElementById(DEFAULT_THEME_LINK_ID)
      if (!link) {
        link = createElement('link', {
          id: DEFAULT_THEME_LINK_ID,
          rel: 'stylesheet',
          href: DEFAULT_CSS_URL,
          crossOrigin: 'anonymous'
        })
        link.onload = () => {
          addLightThemeCSS()
          setTimeout(resolve, 50)
        }
        link.onerror = () => {
          addLightThemeCSS()
          setTimeout(resolve, 50)
        }
        document.head.appendChild(link)
      } else {
        addLightThemeCSS()
        setTimeout(resolve, 50)
      }
    }
  })
}

/**
 * Add light theme inline styles for flatpickr components
 */
export function addLightThemeCSS() {
  const existing = document.getElementById(LIGHT_STYLE_ID)
  if (existing) return

  const lightCSS = `
    .flatpickr-calendar {
      background: #ffffff !important;
      color: #1a1a1a !important;
      border: 1px solid #dadce0 !important;
    }

    .flatpickr-calendar.arrowTopLeft::before,
    .flatpickr-calendar.arrowTopLeft::after { border-left-color: #dadce0 !important; }
    .flatpickr-calendar.arrowTopRight::before,
    .flatpickr-calendar.arrowTopRight::after { border-right-color: #dadce0 !important; }
    .flatpickr-calendar.arrowBottomLeft::before,
    .flatpickr-calendar.arrowBottomLeft::after { border-left-color: #dadce0 !important; }
    .flatpickr-calendar.arrowBottomRight::before,
    .flatpickr-calendar.arrowBottomRight::after { border-right-color: #dadce0 !important; }

    .flatpickr-day { color: #1a1a1a !important; }
    .flatpickr-day:hover,
    .flatpickr-day.prevMonthDay:hover,
    .flatpickr-day.nextMonthDay:hover { background: #f0f0f0 !important; }
    .flatpickr-day.selected,
    .flatpickr-day.startRange,
    .flatpickr-day.endRange { background: #0d6efd !important; color: white !important; }
    .flatpickr-day.inRange { background: #e6f3ff !important; }
    .flatpickr-day.disabled { color: #adb5bd !important; }
    .flatpickr-day.week { color: #1a1a1a !important; }
    .flatpickr-month { background: #ffffff !important; }
    .flatpickr-year select,
    .flatpickr-month select { color: #1a1a1a !important; }
  `

  addStyleElement(LIGHT_STYLE_ID, lightCSS)
}

/**
 * Ensure flatpickr calendar appears above modals with proper z-index CSS rule
 */
export function ensureModalZIndexCSS() {
  const existing = document.getElementById(MODAL_ZINDEX_ID)
  if (existing) return

  const css = `
    .modal ~ .flatpickr-calendar,
    .modal ~ .flatpickr-calendar-wrapper {
      z-index: 99999 !important;
    }
  `

  addStyleElement(MODAL_ZINDEX_ID, css)
}

/**
 * Remove all flatpickr theme elements from the document
 */
export function cleanupThemeCSS() {
  removeElementById(LIGHT_STYLE_ID)
  removeElementById(MODAL_ZINDEX_ID)
  removeElementById(DARK_THEME_LINK_ID)
  removeElementById(DEFAULT_THEME_LINK_ID)
}

/**
 * Apply theme colors to an already-rendered calendar container element
 */
export function applyThemeToCalendar(calendarElement) {
  if (!calendarElement) return

  const theme = getTheme()

  if (theme === 'light') {
    calendarElement.style.setProperty('background-color', '#ffffff', 'important')
    calendarElement.style.setProperty('color', '#1a1a1a', 'important')
    calendarElement.style.setProperty('border-color', '#dadce0', 'important')
  } else {
    calendarElement.style.setProperty('background-color', '#2d2d2d', 'important')
    calendarElement.style.setProperty('color', '#ffffff', 'important')
    calendarElement.style.setProperty('border-color', '#404040', 'important')
  }
}

/* -- helpers -- */

function removeElementById(id) {
  const el = document.getElementById(id)
  if (el) el.remove()
}

function createElement(tag, attrs) {
  const el = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value)
  }
  return el
}

function addStyleElement(id, cssText) {
  const style = document.createElement('style')
  const nonce = document.querySelector('meta[name="csp-nonce"]')?.content
  if (nonce) style.nonce = nonce
  style.id = id
  style.textContent = cssText
  document.head.appendChild(style)
}
